import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mesProductsApi } from '@/api/mesProductsApi'
import { useStoredPreference } from '@/hooks/useStoredPreference'
import {
  PRIMARY_DESCRIPTION_PREF_KEY,
  coercePrimaryDescription,
  isDefaultPrimaryDescription,
} from '@/lib/mesProducts'
import { useTenant } from '@/providers/TenantProvider'
import type { MesProductOption, MesProductOptionField, MesProductPayload } from '@/types/cgt'

/** Prefix key for every Product Code query, for invalidation (z8nrz7fvhx). */
const MES_PRODUCTS_KEY = ['mes-products'] as const

/**
 * The selected tenant's Product Codes, by code. The catalogue is small and
 * fetched whole; search and sort happen client-side. `active: true` is what
 * Create MES order offers.
 */
export function useMesProducts({ active, enabled = true }: { active?: boolean; enabled?: boolean } = {}) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...MES_PRODUCTS_KEY, 'list', selectedTenantId, active ?? 'all'],
    enabled: enabled && !!selectedTenantId,
    queryFn: () => mesProductsApi.list({ active }),
  })
}

/** One Product Code, every field: the Product Information dialog. */
export function useMesProduct(id: string | null) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...MES_PRODUCTS_KEY, 'one', selectedTenantId, id],
    enabled: !!selectedTenantId && !!id,
    queryFn: () => mesProductsApi.get(id!),
  })
}

/** Each selection field's options and the storage units, for the form. */
export function useMesProductReference(enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...MES_PRODUCTS_KEY, 'reference', selectedTenantId],
    enabled: enabled && !!selectedTenantId,
    queryFn: () => mesProductsApi.reference(),
  })
}

/** The selection fields' own option lists, for Configure → Product Code → Option Sets. */
export function useMesProductOptionLists(enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...MES_PRODUCTS_KEY, 'option-lists', selectedTenantId],
    enabled: enabled && !!selectedTenantId,
    queryFn: () => mesProductsApi.optionLists(),
  })
}

/** Create and update, each refreshing every Product Code query on success. */
export function useMesProductMutations() {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: MES_PRODUCTS_KEY })
  }
  const create = useMutation({
    mutationFn: (payload: MesProductPayload) => mesProductsApi.create(payload),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: (input: { id: string; payload: MesProductPayload }) => mesProductsApi.update(input.id, input.payload),
    onSuccess: invalidate,
  })
  // Refreshes the form's reference lists too: they share the prefix.
  const updateOptionList = useMutation({
    mutationFn: (input: { field: MesProductOptionField; options: MesProductOption[] }) =>
      mesProductsApi.updateOptionList(input.field, input.options),
    onSuccess: invalidate,
  })
  return { create, update, updateOptionList }
}

/**
 * Which description the signed-in user reads first (Profile). Not
 * tenant-scoped: it is how a person reads, like their column layouts.
 */
export function usePrimaryDescription() {
  return useStoredPreference(PRIMARY_DESCRIPTION_PREF_KEY, coercePrimaryDescription, isDefaultPrimaryDescription)
}
